import {
	ConstructionSiteOffer,
	ConstructionSiteWarrantyOffer,
	SanitizedWarrantySelection,
	SelectableWarranty,
} from "@dune/types";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@dune/ui";
import { CheckIcon, XIcon } from "lucide-react";

const WarrantiesAccordion = ({
	binderWarranty,
	constructionSiteOffers,
}: {
	binderWarranty: SanitizedWarrantySelection;
	constructionSiteOffers?: ConstructionSiteOffer[];
}) => {
	const constructionSiteOffer = constructionSiteOffers?.find(
		(constructionSiteOffer) =>
			constructionSiteOffer.binderWarranty.id === binderWarranty.id,
	);

	const hasMainWarranties = binderWarranty.warranties.length > 0;
	const visibleDependentWarranties =
		binderWarranty.dependentWarranties?.filter((dependentWarranty) => {
			return (
				!!constructionSiteOffers?.find(
					(cso) => cso.binderWarranty.id === dependentWarranty.id,
				) || dependentWarranty.checked
			);
		}) ?? [];
	const hasVisibleDependentWarranties = visibleDependentWarranties.length > 0;

	if (!hasMainWarranties && !hasVisibleDependentWarranties) return null;

	return (
		<div className="flex flex-col gap-2 w-full">
			<h1 className="text-md font-medium">L'OFFRE COMPREND</h1>
			<div className="flex flex-row gap-6 w-full overflow-x-auto">
				<Accordion
					type="multiple"
					key={binderWarranty.id}
					className="flex flex-col gap-1 w-full rounded-lg border px-4 h-full"
				>
					{hasMainWarranties && (
						<BinderWarrantyWarrantiesAccordionItem
							binderWarranty={binderWarranty}
							constructionSiteWarrantyOffers={
								constructionSiteOffer?.constructionSiteWarrantyOffers
							}
						/>
					)}
					{visibleDependentWarranties.map((dependentWarranty) => (
						<DependentWarrantyWarrantiesAccordionItem
							key={dependentWarranty.id}
							dependentWarranty={dependentWarranty}
							constructionSiteOffer={constructionSiteOffers?.find(
								(constructionSiteOffer) =>
									constructionSiteOffer.binderWarranty.id ===
									dependentWarranty.id,
							)}
						/>
					))}
				</Accordion>
			</div>
		</div>
	);
};

const WarrantiesContainer = ({
	warranties,
	constructionSiteWarrantyOffers,
}: {
	warranties: SelectableWarranty[];
	constructionSiteWarrantyOffers?: ConstructionSiteWarrantyOffer[];
}) => {
	return (
		<div className="flex flex-col gap-2 w-full px-4">
			{warranties.map((warranty) => {
				const checked =
					!!constructionSiteWarrantyOffers?.find(
						(offer) => offer.warranty.id === warranty.id,
					) || warranty.checked;
				return (
					<div key={warranty.id} className="flex flex-row gap-2">
						<div
							className={`flex flex-shrink-0 size-5 d items-center justify-center rounded-full border text-white ${checked ? "bg-green-500" : "bg-gray-300"}`}
						>
							{checked ? <CheckIcon size={14} /> : <XIcon size={14} />}
						</div>
						<p className="flex flex-wrap">{warranty.name}</p>
					</div>
				);
			})}
		</div>
	);
};

const BinderWarrantyWarrantiesAccordionItem = ({
	binderWarranty,
	constructionSiteWarrantyOffers,
}: {
	binderWarranty: SanitizedWarrantySelection;
	constructionSiteWarrantyOffers?: ConstructionSiteWarrantyOffer[];
}) => {
	return (
		<AccordionItem value={String(binderWarranty.id)} className="p-2">
			<AccordionTrigger>
				<div className="flex flex-row gap-2">
					<div
						className={
							"flex flex-shrink-0 size-6 items-center justify-center rounded-full border text-white border-blue-100 bg-green-500"
						}
					>
						<CheckIcon size={16} />
					</div>
					<div className="flex justify-between items-center w-full">
						<p className="text-md font-medium">{binderWarranty.name}</p>
					</div>
				</div>
			</AccordionTrigger>
			<AccordionContent>
				<WarrantiesContainer
					warranties={binderWarranty.warranties}
					constructionSiteWarrantyOffers={constructionSiteWarrantyOffers}
				/>
			</AccordionContent>
		</AccordionItem>
	);
};

const DependentWarrantyWarrantiesAccordionItem = ({
	dependentWarranty,
	constructionSiteOffer,
}: {
	dependentWarranty: SanitizedWarrantySelection;
	constructionSiteOffer?: ConstructionSiteOffer;
}) => {
	return (
		<AccordionItem
			value={String(dependentWarranty.id)}
			key={dependentWarranty.id}
			className="p-2"
		>
			<AccordionTrigger>
				<div className="flex flex-row gap-2">
					<div
						className={
							"flex flex-shrink-0 size-6 items-center justify-center rounded-full border text-white border-blue-100 bg-green-500"
						}
					>
						<CheckIcon size={16} />
					</div>
					<div className="flex justify-between items-center w-full">
						<p className="text-md font-medium">{dependentWarranty.name}</p>
					</div>
				</div>
			</AccordionTrigger>
			<AccordionContent>
				<WarrantiesContainer
					warranties={dependentWarranty.warranties}
					constructionSiteWarrantyOffers={
						constructionSiteOffer?.constructionSiteWarrantyOffers
					}
				/>
			</AccordionContent>
		</AccordionItem>
	);
};

export default WarrantiesAccordion;
